import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { gameTypes } from '../data/GameTypes';
import useAuth from './useAuth';

export default function Games() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleCreate = (gameType) => {
    if (user) {
      navigate(`/studio/new?gameType=${gameType}`);
    } else {
      navigate('/register');
    }
  };

  const getIconColorClasses = (gameId) => {
    // Updated to use retro gaming colors
    const colorMap = {
      "pindrop": {
        bg: "rgba(231, 76, 60, 0.2)",
        border: "rgba(231, 76, 60, 0.4)",
        color: "#E74C3C"
      },
      "hint": {
        bg: "rgba(241, 196, 15, 0.2)",
        border: "rgba(241, 196, 15, 0.4)",
        color: "#F1C40F"
      },
      "quiz": {
        bg: "rgba(52, 152, 219, 0.2)",
        border: "rgba(52, 152, 219, 0.4)",
        color: "#3498DB"
      },
      "mapselect": {
        bg: "rgba(46, 204, 113, 0.2)",
        border: "rgba(46, 204, 113, 0.4)",
        color: "#2ECC71"
      },
      "snake": {
        bg: "rgba(155, 89, 182, 0.2)",
        border: "rgba(155, 89, 182, 0.4)",
        color: "#9B59B6"
      }
    };
    
    return colorMap[gameId] || colorMap["quiz"];
  };

  return (
    <div className="max-w-7xl mx-auto" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      {/* Header and Introduction */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-black tracking-wider flex items-center gap-3" style={{
          color: '#F1C40F',
          textShadow: '2px 2px 0px #E67E22, 4px 4px 0px #D35400',
          WebkitTextStroke: '1px #E67E22'
        }}>
          GAME LIBRARY
        </h1>
        <p className="text-sm mt-2 font-bold" style={{color: '#ECF0F1'}}>
          Choose from our collection of engaging interactive games - trivia, puzzles, word games, and more
        </p>
      </div>

      {/* Game Cards Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {gameTypes.map((game) => {
          const colors = getIconColorClasses(game.id);
          return (
            <div 
              key={game.name}
              className="studio-card rounded-xl overflow-hidden transition-all duration-200 flex flex-col h-full"
              style={{
                fontFamily: '"Share Tech Mono", monospace'
              }}
            >
              {/* Header Section with Icon */}
              <div className="p-6 pb-0">
                <div className="flex items-center gap-3 mb-3">
                  {game.icon && (
                    <div 
                      className="p-2.5 rounded-lg"
                      style={{
                        background: colors.bg,
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      <game.icon className="w-5 h-5" style={{color: colors.color}} />
                    </div>
                  )}
                  <h3 className="text-lg font-black tracking-wide" style={{
                    color: '#F1C40F',
                    textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
                  }}>
                    {game.name.toUpperCase()}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 pb-6 flex flex-col flex-1">
                <p className="text-sm mb-6 flex-1 font-bold leading-relaxed" style={{color: '#BDC3C7'}}>
                  {game.description}
                </p>

                {/* Buttons */}
                <div className="flex gap-3 mt-auto">
                  <a
                    href={`/play/${game.exampleId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-2.5 px-3 text-sm rounded-lg font-bold text-center transition-all duration-100"
                    style={{
                      background: 'rgba(52, 73, 94, 0.6)',
                      border: '1px solid rgba(149, 165, 166, 0.4)',
                      color: '#BDC3C7'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(149, 165, 166, 0.1)';
                      e.target.style.color = '#ECF0F1';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(52, 73, 94, 0.6)';
                      e.target.style.color = '#BDC3C7';
                    }}
                  >
                    TRY DEMO ↗
                  </a>
                  <button
                    onClick={() => handleCreate(game.id)}
                    className="flex-1 py-2.5 px-3 text-sm rounded-lg font-bold transition-all duration-100"
                    style={{
                      background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                      border: '2px solid rgba(241, 196, 15, 0.6)',
                      color: '#FFFFFF',
                      textShadow: '1px 1px 0px #A93226'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #C0392B, #A93226)';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #E74C3C, #C0392B)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    START CAMPAIGN
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}